import React, { useMemo, useState } from 'react';
import _t from 'counterpart';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { extractErrorMessage, formatCurrency, formatDateTime, formatPercentage, isSet } from '../../helpers';
import {
  disableWallet,
  enableWallet,
  fetchWallet,
  updatePropAccountProgressStatus,
} from '../../services/BackendService';
import PageLayout from '../../components/PageLayout';
import {
  CBadge,
  CCard,
  CCardBody,
  CCardHeader,
  CCardTitle,
  CCol,
  CDropdown,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
  CLink,
  CRow,
} from '@coreui/react';
import Loading from '../../components/Loading';
import Balance from '../customers/CustomerWalletBalance';
import Credit from '../customers/CustomerWalletCredit';
import PerformanceChart from './PerformanceChart';
import Error from '../../components/Error';
import ConfirmationModal from '../../components/ConfirmationModal';
import toast from 'react-hot-toast';
import { TradingType, WalletType } from '../customers/types';
import PlatformBadge from '../../components/PlatformBadge';
import PropAccountChallengeInfo from './PropAccountObjectives';
import PropFundedAccountInfo from './PropFundedAccountInfo';
import {
  IFundedAccountWithBalance,
  IPropChallengeAccount,
  isGatewayWallet,
  IWalletWithBalance,
  PropChallengeAccountStatus,
  WalletStatus,
} from './types';
import { IChallengeLimit, IChallengeObjective } from '../propTrading/types';
import { useAppSelector, useLiveAccountUpdates } from '../../helpers/customHooks';
import { calculateMarginLevel } from '../../helpers/calculations';
import { Permission, UserRole } from '../../reducers/userReducer';

export interface IPropChallengeData {
  propChallengeId: number;
  propStatus: PropChallengeAccountStatus;
  walletLimits: Array<IChallengeLimit>;
  walletObjectives: Array<IChallengeObjective>;
}

const WalletPage = () => {
  const [showEnableWallet, setShowEnableWallet] = useState<boolean>(false);
  const [showDisableWallet, setShowDisableWallet] = useState<boolean>(false);
  const [showPassChallenge, setShowPassChallenge] = useState<boolean>(false);
  const [showFailChallenge, setShowFailChallenge] = useState<boolean>(false);

  const queryClient = useQueryClient();
  const params = useParams();
  const { id } = params as any;
  const permissions = useAppSelector((state) => state.user.permissions);
  const roles = useAppSelector((state) => state.user.roles);
  const isAuditAgent = roles.includes(UserRole.AuditAgent);
  const canManageWallets = permissions.includes(Permission.MANAGE_WALLETS);

  const {
    data: walletData,
    isLoading,
    isIdle,
    isError,
    refetch,
  } = useQuery(['customer-wallet', id], () => fetchWallet(id), {
    onError: (e: any) => {
      const error = extractErrorMessage(e);
      toast.error(error);
    },
    retry: false,
  });

  // False until the wallet is known: an MT5 page would otherwise subscribe on the loading render and
  // tear the subscription down again as soon as the platform arrives.
  useLiveAccountUpdates(id, walletData !== undefined && isGatewayWallet(walletData.wallet));

  const enableWalletMutation = useMutation(() => enableWallet(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(['customer-wallet', id]);
      setShowEnableWallet(false);
      toast.success(_t('wallet.wallet-enabled'));
    },
    onError: (e: any) => {
      toast.error(extractErrorMessage(e));
    },
  });

  const disableWalletMutation = useMutation(() => disableWallet(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(['customer-wallet', id]);
      setShowDisableWallet(false);
      toast.success(_t('wallet.wallet-disabled'));
    },
    onError: (e: any) => {
      toast.error(extractErrorMessage(e));
    },
  });

  const updatePropChallengeProgressStatusMutation = useMutation(
    (status: string) => updatePropAccountProgressStatus(id, status),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['customer-wallet', id]);
        queryClient.invalidateQueries(['prop-challenge-account-progress']);
        toast.success(_t('wallet.challenge-status-updated'));
      },
      onError: (e: any) => {
        toast.error(extractErrorMessage(e));
      },
    }
  );

  const handleModalVisibility = useMemo(
    () => ({
      enable: {
        show: () => setShowEnableWallet(true),
        hide: () => setShowEnableWallet(false),
      },
      disable: {
        show: async () => {
          if (wallet.type === WalletType.PropChallenge && wallet.status === WalletStatus.Active) {
            toast.error(_t('wallet.prop-challenge-cannot-disable'));
            return;
          }
          setShowDisableWallet(true);
        },
        hide: () => setShowDisableWallet(false),
      },
      passChallenge: {
        show: () => setShowPassChallenge(true),
        hide: () => setShowPassChallenge(false),
        confirm: () => {
          updatePropChallengeProgressStatusMutation.mutate(PropChallengeAccountStatus.Passed);
          setShowPassChallenge(false);
        },
      },
      failChallenge: {
        show: () => setShowFailChallenge(true),
        hide: () => setShowFailChallenge(false),
        confirm: () => {
          updatePropChallengeProgressStatusMutation.mutate(PropChallengeAccountStatus.Failed);
          setShowFailChallenge(false);
        },
      },
    }),
    [
      setShowEnableWallet,
      setShowDisableWallet,
      setShowPassChallenge,
      setShowFailChallenge,
      updatePropChallengeProgressStatusMutation,
    ]
  );

  if (isLoading || isIdle || !walletData) {
    return <Loading />;
  }

  if (isError) {
    return <Error onRetry={refetch} />;
  }

  const { wallet, history, limits, objectives } = walletData;

  // Both platforms now carry balances on the detail response — Wintrado's from the trading
  // gateway, MT5's from the mt5-bridge — so the money fields below are no longer Wintrado-only.
  const gwWallet = wallet as IWalletWithBalance;
  const marginLevel = calculateMarginLevel(gwWallet.equity, gwWallet.margin);
  // On MT5 the three money figures are read together off the same bridge call and are what an
  // operator reconciles against the terminal, so they sit together rather than one per column.
  const isMt5 = !isGatewayWallet(wallet);
  return (
    <PageLayout
      title={_t('wallet.title')}
      titleAppend={
        <div className="d-flex justify-content-between float-right mb-2">
          <div className="d-flex flex-nowrap flex-row-reverse">
            {wallet.type !== WalletType.Affiliate && canManageWallets && (
              <CDropdown className="float-right">
                <CDropdownToggle caret color="primary">
                  {_t('wallet.options')}
                </CDropdownToggle>
                <CDropdownMenu>
                  {wallet.status === WalletStatus.Disabled && (
                    <CDropdownItem onClick={handleModalVisibility.enable.show}>
                      {_t('wallet.enable-wallet')}
                    </CDropdownItem>
                  )}
                  {wallet.status === WalletStatus.Active && (
                    <CDropdownItem onClick={handleModalVisibility.disable.show}>
                      {_t('wallet.disable-wallet')}
                    </CDropdownItem>
                  )}
                  {wallet.status !== WalletStatus.Disabled && wallet.type === WalletType.PropChallenge && (
                    <CDropdownItem onClick={handleModalVisibility.passChallenge.show}>
                      {_t('wallet.pass-challenge')}
                    </CDropdownItem>
                  )}
                  {wallet.status !== WalletStatus.Disabled && wallet.type === WalletType.PropChallenge && (
                    <CDropdownItem onClick={handleModalVisibility.failChallenge.show}>
                      {_t('wallet.fail-challenge')}
                    </CDropdownItem>
                  )}
                </CDropdownMenu>
              </CDropdown>
            )}
          </div>
        </div>
      }
    >
      <CRow>
        <CCol lg={6}>
          <CCard>
            <CCardHeader>
              <CCardTitle className="float-left">{_t('global.info')}</CCardTitle>
              <div className="float-right">
                {wallet.type === WalletType.Affiliate ? (
                  '-'
                ) : (
                  <CBadge
                    style={{ backgroundColor: wallet.tradingType === TradingType.Netting ? '#18B8C2' : '#9618C2' }}
                  >
                    {wallet.tradingType === TradingType.Netting ? _t('wallets.netting') : _t('wallets.hedging')}
                  </CBadge>
                )}
              </div>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol md={6}>
                  <dl>
                    <dt>{_t('wallets.customer-name')}:</dt>
                    <dd>
                      <CLink href={`/customers/${wallet.customer.id}`}>{wallet.customer.name}</CLink>
                    </dd>
                    <dt>{_t('global.status')}:</dt>
                    <dd>
                      <CBadge color={wallet.status.toLowerCase()}>{wallet.status}</CBadge>
                    </dd>
                    <dt>{_t('customer.wallets.type')}:</dt>
                    <dd>{wallet.type}</dd>

                    <dt>{_t('customer.wallets.accountId')}:</dt>
                    <dd>{wallet.id}</dd>

                    <dt>{_t('global.created-at')}:</dt>
                    <dd>{wallet.createdAt ? formatDateTime(wallet.createdAt) : '-'}</dd>

                    <dt>{_t('customer.wallets.currency')}:</dt>
                    <dd>{wallet.currency}</dd>

                    <dt>{_t('customer.wallets.platform')}:</dt>
                    <dd>
                      <PlatformBadge platform={wallet.platform} />
                    </dd>

                    <dt>{_t('customer.wallets.balance')}:</dt>
                    <dd>
                      {gwWallet.balance ? <Balance value={gwWallet.balance} currency={wallet.currency} /> : 'n/a'}
                    </dd>

                    {isMt5 && (
                      <>
                        <dt>{_t('customer.wallets.credit')}:</dt>
                        <dd>
                          {gwWallet.credit ? <Credit value={gwWallet.credit} currency={wallet.currency} /> : 'n/a'}
                        </dd>

                        {wallet.type !== WalletType.Affiliate && (
                          <>
                            <dt>{_t('wallet.equity')}:</dt>
                            <dd>{formatCurrency(gwWallet.equity, wallet.currency)}</dd>
                          </>
                        )}
                      </>
                    )}

                    {wallet.type !== WalletType.Affiliate && (
                      <>
                        <dt>{_t('wallet.margin')}:</dt>
                        <dd>{formatCurrency(gwWallet.margin, wallet.currency)}</dd>

                        <dt>{_t('wallet.profit-loss')}:</dt>
                        <dd>
                          {gwWallet.profitLoss === undefined
                            ? _t('wallet.no-open-positions')
                            : formatCurrency(gwWallet.profitLoss, wallet.currency)}
                        </dd>
                      </>
                    )}

                    {wallet.propFunnelId && wallet.type === WalletType.PropFunded && !wallet.isDemo && (
                      <>
                        <dt>{_t('wallet.trader-profit-share-factor')}:</dt>
                        <dd>{Number((wallet as IFundedAccountWithBalance).traderProfitShareFactor) * 100}%</dd>
                      </>
                    )}
                  </dl>
                </CCol>
                <CCol md={6}>
                  <dl>
                    {/* Shown beside the balance for MT5 — see `isMt5` above. */}
                    {!isMt5 && (
                      <>
                        <dt>{_t('customer.wallets.credit')}:</dt>
                        <dd>
                          {gwWallet.credit ? <Credit value={gwWallet.credit} currency={wallet.currency} /> : 'n/a'}
                        </dd>
                      </>
                    )}

                    {wallet.type !== WalletType.Affiliate && (
                      <>
                        <dt>{_t('wallet-modal.group')}:</dt>
                        <dd>
                          {/* MT5 groups have no detail page: everything on it is gateway data the
                              MT5 server owns instead, and the endpoint rejects them. */}
                          {isAuditAgent || !isGatewayWallet(wallet) ? (
                            wallet.group.name
                          ) : (
                            <CLink href={`/groups/${wallet.group.id}`}>{wallet.group.name}</CLink>
                          )}
                        </dd>

                        {!isMt5 && (
                          <>
                            <dt>{_t('wallet.equity')}:</dt>
                            <dd>{formatCurrency(gwWallet.equity, wallet.currency)}</dd>
                          </>
                        )}

                        <dt>{_t('wallet.free-margin')}:</dt>
                        <dd>{formatCurrency(gwWallet.marginFree, wallet.currency)}</dd>

                        <dt>{_t('wallet.margin-level')}:</dt>
                        <dd>{isSet(marginLevel) ? formatPercentage(marginLevel) : '-'}</dd>
                      </>
                    )}

                    {wallet.propFunnelId && (
                      <>
                        <dt>{_t('wallet.prop-funnel-id')}:</dt>
                        <dd>
                          {isAuditAgent ? wallet.propFunnelId : <CLink href={`/prop-funnels/${wallet.propFunnelId}`}>{wallet.propFunnelId}</CLink>}
                        </dd>
                      </>
                    )}

                    {wallet.type === WalletType.PropChallenge && (
                      <>
                        <dt>{_t('wallet.prop-stage')}:</dt>
                        <dd>
                          {isAuditAgent ? (
                            `${(wallet as IPropChallengeAccount).currentStage}/${(wallet as IPropChallengeAccount).maxStage}`
                          ) : (
                            <CLink href={`/prop-challenges/${(wallet as IPropChallengeAccount).propChallengeId}`}>
                              {`${(wallet as IPropChallengeAccount).currentStage}/${
                                (wallet as IPropChallengeAccount).maxStage
                              }`}
                            </CLink>
                          )}
                        </dd>
                      </>
                    )}

                    {wallet.propFunnelId && wallet.type === WalletType.PropFunded && (
                      <>
                        <dt>{_t('wallet.prop-stage')}:</dt>
                        <dd>
                          {isAuditAgent ? (
                            `${(wallet as IFundedAccountWithBalance).currentStage}/${
                              (wallet as IFundedAccountWithBalance).maxStage
                            }`
                          ) : (
                            <CLink
                              href={`/funded-account-types/${(wallet as IFundedAccountWithBalance).fundedAccountTypeId}`}
                            >
                              {`${(wallet as IFundedAccountWithBalance).currentStage}/${
                                (wallet as IFundedAccountWithBalance).maxStage
                              }`}
                            </CLink>
                          )}
                        </dd>

                        <dt>{_t('wallet.funded-account-behavior')}:</dt>
                        <dd>{(wallet as IFundedAccountWithBalance).behavior}</dd>

                        <dt>{_t('funded-account-types.is-demo')}:</dt>
                        <dd>{wallet.isDemo ? _t('global.yes') : _t('global.no')}</dd>
                      </>
                    )}
                    {wallet.propFunnelId && wallet.type === WalletType.PropFunded && (
                      <>
                        <dt>{_t('wallet.available-withdrawal-funds')}:</dt>
                        <dd>
                          {formatCurrency(
                            (wallet as IFundedAccountWithBalance).availableWithdrawalFunds,
                            wallet.currency
                          )}
                        </dd>
                      </>
                    )}
                  </dl>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol lg={6}>
          <CCard>
            <CCardHeader>
              <CCardTitle>{_t('wallet.wallet-performance')}</CCardTitle>
            </CCardHeader>
            <CCardBody>
              <PerformanceChart data={history} />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      {wallet.type === WalletType.PropChallenge && limits && objectives && (
        <PropAccountChallengeInfo
          propChallengeId={(wallet as IPropChallengeAccount).propChallengeId}
          propStatus={(wallet as IPropChallengeAccount).propStatus}
          walletLimits={limits}
          walletObjectives={objectives}
        />
      )}
      {wallet.type === WalletType.PropFunded && limits && objectives && (
        <PropFundedAccountInfo limits={limits} objectives={objectives} />
      )}
      <ConfirmationModal
        id={id}
        show={showEnableWallet}
        onConfirm={enableWalletMutation.mutate}
        hidePanel={handleModalVisibility.enable.hide}
        title={_t('wallet.enable-wallet')}
        error={null}
        buttonColor="primary"
        withMutation={false}
        isLoading={enableWalletMutation.isLoading}
      />
      <ConfirmationModal
        id={id}
        show={showDisableWallet}
        onConfirm={disableWalletMutation.mutate}
        hidePanel={handleModalVisibility.disable.hide}
        title={_t('wallet.disable-wallet')}
        error={null}
        buttonColor="primary"
        withMutation={false}
        isLoading={disableWalletMutation.isLoading}
      />
      <ConfirmationModal
        id={id}
        show={showPassChallenge}
        onConfirm={handleModalVisibility.passChallenge.confirm}
        hidePanel={handleModalVisibility.passChallenge.hide}
        title={_t('wallet.pass-challenge-confirmation')}
        error={null}
        buttonColor="primary"
        withMutation={false}
        isLoading={updatePropChallengeProgressStatusMutation.isLoading}
      />
      <ConfirmationModal
        id={id}
        show={showFailChallenge}
        onConfirm={handleModalVisibility.failChallenge.confirm}
        hidePanel={handleModalVisibility.failChallenge.hide}
        title={_t('wallet.fail-challenge-confirmation')}
        error={null}
        buttonColor="primary"
        withMutation={false}
        isLoading={updatePropChallengeProgressStatusMutation.isLoading}
      />
    </PageLayout>
  );
};

export default WalletPage;
